import React, { useState } from "react";
import { toast } from "react-hot-toast";

// Components
import DeleteButton from "../../../../shared/components/DeleteButton";
import AuthenticatedLayout from "../../../../shared/layouts/AuthenticatedLayout";
import theme from "../../../../shared/utils/themeColors";

// Hooks
import { useQuestionCategories } from "../../hooks/useQuestionCategories";

// Types
import { QuestionCategory } from "../../types/question-bank.types";

// Color options for categories
const colorOptions = [
  { value: "", label: "Default (No Color)" },
  { value: "#EF4444", label: "Red" },
  { value: "#F59E0B", label: "Orange" },
  { value: "#10B981", label: "Green" },
  { value: "#3B82F6", label: "Blue" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#EC4899", label: "Pink" },
  { value: "#6B7280", label: "Gray" },
];

// Color picker component
const ColorPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div className="flex items-center space-x-2">
        <div
          className="w-8 h-8 rounded-md border border-gray-300 cursor-pointer flex items-center justify-center"
          style={{ backgroundColor: value || "#ffffff" }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {!value && <span className="text-gray-400">×</span>}
        </div>
        <button
          type="button"
          className="text-sm text-gray-500 hover:text-gray-700"
          onClick={() => setIsOpen(!isOpen)}
        >
          {value ? "Change color" : "Select color"}
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-2 border border-gray-200 grid grid-cols-4 gap-1">
          <div
            className="w-6 h-6 rounded-md border border-gray-300 cursor-pointer flex items-center justify-center"
            onClick={() => {
              onChange("");
              setIsOpen(false);
            }}
          >
            <span className="text-gray-400">×</span>
          </div>
          {colorOptions
            .filter((c) => c.value)
            .map((color) => (
              <div
                key={color.value}
                className="w-6 h-6 rounded-md cursor-pointer"
                style={{ backgroundColor: color.value }}
                onClick={() => {
                  onChange(color.value);
                  setIsOpen(false);
                }}
                title={color.label}
              />
            ))}
        </div>
      )}
    </div>
  );
};

function Categories() {
  // Use our custom hook for categories
  const {
    categories,
    hierarchy,
    loading,
    error,
    refresh,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useQuestionCategories();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [currentCategory, setCurrentCategory] =
    useState<QuestionCategory | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parentId: "",
    color: "",
  });
  const itemsPerPage = 10;

  const renderCategoryHierarchy = (categories: any[], level = 0): any[] => {
    let result: any[] = [];

    categories.forEach((category) => {
      result.push({
        ...category,
        _level: level,
        _indentation: level * 20,
      });

      if (category.subcategories && category.subcategories.length > 0) {
        result = [
          ...result,
          ...renderCategoryHierarchy(category.subcategories, level + 1),
        ];
      }
    });

    return result;
  };

  const flattenedHierarchy = hierarchy
    ? renderCategoryHierarchy(hierarchy)
    : [];

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(0); // Reset to first page when searching
  };

  // Handle refresh
  const handleRefresh = () => {
    refresh();
  };

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Open add modal
  const handleAddNew = () => {
    setFormData({
      name: "",
      description: "",
      parentId: "",
      color: "",
    });
    setShowAddModal(true);
  };

  // Open edit modal
  const handleEdit = (category: QuestionCategory) => {
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      parentId: category.parentId || "",
      color: (category as any).color || "", // Type assertion to avoid TypeScript error
    });
    setShowEditModal(true);
  };

  // Handle add category
  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Use type assertion to avoid TypeScript error
      await createCategory({
        name: formData.name,
        description: formData.description,
        parentId: formData.parentId || null,
        color: formData.color || null,
      } as any);
      setShowAddModal(false);
      toast.success("Category added successfully");
    } catch (error) {
      toast.error("Failed to add category");
    }
  };

  // Handle update category
  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCategory) return;

    try {
      // Use type assertion to avoid TypeScript error
      await updateCategory(currentCategory.id, {
        name: formData.name,
        description: formData.description,
        parentId: formData.parentId || null,
        color: formData.color || null,
      } as any);

      setShowEditModal(false);
      toast.success("Category updated successfully");
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      await deleteCategory(id);
      toast.success("Category deleted successfully");
    } catch (error) {
      toast.error(error.details || "Failed to delete category");
    }
  };

  return (
    <>
      <AuthenticatedLayout
        isLoading={loading}
        error={error}
        isEmpty={!hierarchy?.length}
        emptyStateProps={{
          title: "No categories found",
          description: searchTerm
            ? "No categories match your search criteria"
            : "Start by adding your first category",
          icon: (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className={`w-8 h-8 ${theme.text.muted}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          ),
        }}
        pageHeader={{
          title: "Question Categories",
          search: {
            show: true,
            placeholder: "Search categories...",
            onChange: handleSearch,
          },
          refresh: {
            show: true,
            onRefresh: handleRefresh,
          },
          actions: [
            {
              label: "Add Category",
              onClick: handleAddNew,
            },
          ],
        }}
      >
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          {/* Category Hierarchy Table */}
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-sm border border-gray-200 mb-8">
            <header className="px-5 py-4">
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">
                Category Hierarchy
              </h2>
            </header>
            <div className="overflow-x-auto">
              <table className="table-auto w-full">
                <thead className="text-xs font-semibold uppercase text-gray-500 bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Name</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Level</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Description</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-[220px]">
                      <div className="font-semibold text-center">Actions</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-100">
                  {flattenedHierarchy.map((category) => (
                    <tr key={category.id}>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {(category as any).color && (
                            <div
                              className="w-3 h-5 mr-1 flex-shrink-0"
                              style={{
                                backgroundColor: (category as any).color,
                              }}
                            ></div>
                          )}
                          <div
                            className="font-medium text-gray-800 dark:text-gray-100"
                            style={{
                              paddingLeft: `${category._indentation}px`,
                            }}
                          >
                            {category.name}
                            {category.subcategories?.length > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({category.subcategories.length})
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">{category.level}</div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left max-w-xs truncate">
                          {category.description || "-"}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-[220px]">
                        <div className="flex justify-center space-x-1">
                          <button
                            className="text-blue-500 hover:text-blue-600"
                            onClick={() => handleEdit(category)}
                          >
                            <span className="sr-only">Edit</span>
                            <svg
                              className="w-5 h-5 fill-current"
                              viewBox="0 0 20 20"
                            >
                              <path d="M12.3 3.7l4 4L4 20H0v-4L12.3 3.7zm1.4-1.4L16 0l4 4-2.3 2.3-4-4z" />
                            </svg>
                          </button>
                          <DeleteButton
                            onDelete={() => handleDelete(category.id)}
                          />
                          <button
                            className="text-green-500 hover:text-green-600"
                            onClick={() => {
                              setFormData({
                                name: "",
                                description: "",
                                parentId: category.id,
                                color: "",
                              });
                              setShowAddModal(true);
                            }}
                          >
                            <span className="sr-only">Add Subcategory</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-5 h-5 fill-current"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </AuthenticatedLayout>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">
              {formData.parentId ? "Add Subcategory" : "Add New Category"}
            </h2>
            <form onSubmit={handleAddCategory}>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="name"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="description"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="color"
                >
                  Color
                </label>
                <ColorPicker
                  value={formData.color}
                  onChange={(color) => setFormData({ ...formData, color })}
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="parentId"
                >
                  Parent Category
                </label>
                <select
                  id="parentId"
                  name="parentId"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.parentId}
                  onChange={handleInputChange}
                >
                  <option value="">None (Top Level)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className={`${theme.button.secondary}`}
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={`${theme.button.primary}`}>
                  Add Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && currentCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">
              Edit Category: {currentCategory.name}
            </h2>
            <form onSubmit={handleUpdateCategory}>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="edit-name"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="edit-name"
                  name="name"
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="edit-description"
                >
                  Description
                </label>
                <textarea
                  id="edit-description"
                  name="description"
                  rows={3}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="edit-color"
                >
                  Color
                </label>
                <ColorPicker
                  value={formData.color}
                  onChange={(color) => setFormData({ ...formData, color })}
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="edit-parentId"
                >
                  Parent Category
                </label>
                <select
                  id="edit-parentId"
                  name="parentId"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.parentId}
                  onChange={handleInputChange}
                >
                  <option value="">None (Top Level)</option>
                  {categories
                    .filter((cat) => cat.id !== currentCategory.id)
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className={`${theme.button.secondary}`}
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={`${theme.button.primary}`}>
                  Update Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Categories;
